import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type AxiosResponse, AxiosError, CanceledError } from 'axios'
import { nanoid } from 'nanoid'
import { layoutStore, toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { IS_APP_LIGHT } from 'shared/config'
import { type PageLayoutStore } from 'shared/layout'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { ConversationsApi } from 'entities/Conversation'
import { type Conversation } from 'entities/Conversation/model/Conversation'
import { type IResponseContact } from 'entities/Contacts/api/types'
import type {
  IParamsConversationsBatchQueueClose,
  IParamsConversationsBatchQueueFavorite,
  IParamsConversationsBatchQueueOpen,
  IParamsConversationsBatchQueueRead,
  IParamsConversationsBatchQueueUnread,
  IParamsConversationsList,
  IResponseConversation,
  IResponseConversations,
} from 'entities/Conversation/api/types'
import { type IResponseUser } from 'entities/Users/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { sentEventSentry } from 'entities/Sentry'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { SmartViewsApi } from 'entities/SmartViews/api/smartViews'
import { type IParamsSmartViewConversations } from 'entities/SmartViews/api/types'
import { FiltersStore } from 'features/DropdownFilter'
import { OrdersStore } from 'features/DropdownOrder'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import { TransferConversationModalStore } from 'widgets/TransferConversationModal'
import { ConversationAssignDropdownStore } from 'pages/conversations/ui/ConversationAssignDropdown'
import { type ConversationSearchStore } from 'pages/conversations/ui/ConversationHeaderSearch'
import { type IMarkAsNeedResponseParams } from 'pages/conversations/ui/ConversationList/store/types'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'

type IConversationListStoreProps = {
  onChangeLoadingConversations?: (value: boolean) => void
}

export class ConversationListStore {
  private _itemsOrder: number[] = []
  private _topItemsOrder: number[] = []
  private _loading = true
  private _loadingConversations = true
  private _loadingMore = false

  private _hasMore = false
  private _offset = 0
  private _limit = 25
  private _scrollPosition = 0
  private _scrollTrigger = nanoid()

  private _visibleUI = false
  private _firstLoading = true
  private readonly _filtersStore: FiltersStore
  private _ordersStore = new OrdersStore()
  private _cancelRequestConversation = new RequestCancelAxios()
  private _cancelRequestConversations = new RequestCancelAxios()
  private _loadConversationsRequest: Promise<IResponseConversations | undefined> | null = null
  private _disposeInbox: IReactionDisposer | null = null
  private _disposeConversationNew: IReactionDisposer | null = null
  private _disposeLoadingConversations: IReactionDisposer | null = null
  private readonly _conversationAssignDropdownStore: ConversationAssignDropdownStore
  private _contactsTagsModalStore: ContactsTagsModalStore
  private readonly _transferConversationModalStore: TransferConversationModalStore
  private _config: IConversationListStoreProps | null = null
  private _lastCheckedConversationId: number | null = null

  constructor(
    private _pageLayoutStore: PageLayoutStore,
    private _conversationSearchStore: ConversationSearchStore,
    private _entities: IEntitiesStore
  ) {
    this._transferConversationModalStore = new TransferConversationModalStore(this._entities)
    this._conversationAssignDropdownStore = new ConversationAssignDropdownStore(this)
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)

    const {
      featureFlagsStore: { hide_duplicated_conversations_filters },
    } = this._entities

    this._filtersStore = new FiltersStore(this._entities, {
      disableQuickFilters: hide_duplicated_conversations_filters,
    })

    makeAutoObservable(this)
  }

  setConfig = (config: IConversationListStoreProps) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  get entities() {
    return this._entities
  }

  get conversationStore() {
    return this._entities.conversationStore
  }

  get filtersStore() {
    return this._filtersStore
  }

  get ordersStore() {
    return this._ordersStore
  }

  get config() {
    return this._config
  }

  get hasSelected() {
    return !!this.selectedCount
  }

  get listCount() {
    return this.listOrders.length
  }

  get selectedCount() {
    return this.listOrders.filter((item) => item.checked).length
  }

  get isSelectedAll() {
    return this.selectedCount === this.listCount
  }

  get isSelectedNeedResponse() {
    return this._filtersStore.filter?.key === 'pending'
  }

  get isOutboundFilter() {
    return this._filtersStore.isOutbound
  }

  get showConversationsCount() {
    return (
      this.isQuickFilter &&
      this.entities.inboxesStore.isUnifiedInbox &&
      this.entities.inboxesStore.localInboxes.currentInboxId !== 'all'
    )
  }

  get isQuickFilter() {
    if (!this._entities.inboxesStore.localInboxes.currentInboxId) return false
    if (typeof this._entities.inboxesStore.localInboxes.currentInboxId !== 'string') return false

    return this._filtersStore.isQuickFilter(this._entities.inboxesStore.localInboxes.currentInboxId)
  }

  get listOrders() {
    return [...this._topItemsOrder, ...this._itemsOrder].reduce<Conversation[]>((state, id) => {
      const item = this.conversationStore.getItem(id)

      return item ? [...state, item] : state
    }, [])
  }

  get isEmpty() {
    return this.listOrders.length === 0
  }

  get firstConversationId() {
    return this.listOrders?.[0]?.id || null
  }

  get autoSelectConversationId() {
    const conversation = this.listOrders.find(
      (item) => item.id === this.conversationStore.prevItemId
    )

    if (conversation) return conversation.id

    return this.firstConversationId
  }

  get selectedList() {
    return this.listOrders.filter((item) => item.checked)
  }

  get selectedListIds() {
    return this.listOrders.filter((item) => item.checked).map((item) => item.id)
  }

  get selectedContactsListIds() {
    const contactIdsMap = new Map<number, number>()

    this.selectedList.map((conversation) => {
      contactIdsMap.set(conversation.contact_id, conversation.contact_id)

      conversation.participantsIds.map((contactId) => {
        contactIdsMap.set(contactId, contactId)
      })
    })

    return Array.from(contactIdsMap.values())
  }

  get selectedHasOpenConversation() {
    let status = false

    this.selectedList.forEach((item) => {
      if (!item.isClosed) return (status = true)
    })

    return status
  }

  get selectedHasReadConversation() {
    return Boolean(this.selectedList.find((item) => item.isRead))
  }

  get selectedHasUnfavoriteConversation() {
    let status = false

    this.selectedList.forEach((item) => {
      if (!item.is_favorite) return (status = true)
    })

    return status
  }

  get scrollTrigger() {
    return this._scrollTrigger
  }

  get scrollPosition() {
    return this._scrollPosition
  }

  get transferConversationModalStore() {
    return this._transferConversationModalStore
  }

  handleScrollPosition = (value: number) => {
    this._scrollPosition = value
  }

  handleScrollTrigger = () => {
    this._scrollTrigger = nanoid()
  }

  handleResetSelect = () => {
    this.selectedList.forEach((item) => {
      item.handleChecked(false)
    })
    this._lastCheckedConversationId = null
  }

  handleSelectAll = () => {
    this.listOrders.forEach((item) => {
      item.handleChecked(true)
    })
    this._lastCheckedConversationId = null
  }

  handleConversationCheck = (conversation: Conversation, isShiftKey: boolean) => {
    if (isShiftKey && !conversation.checked && this._lastCheckedConversationId !== null) {
      const orders = this.listOrders
      const lastIndex = orders.findIndex((item) => item.id === this._lastCheckedConversationId)
      const currentIndex = orders.findIndex((item) => item.id === conversation.id)

      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex)
        const end = Math.max(lastIndex, currentIndex)

        for (let i = start; i <= end; i++) {
          orders[i].handleChecked(true)
        }

        this._lastCheckedConversationId = conversation.id
        return
      }
    }

    conversation.handleChecked()
    this._lastCheckedConversationId = conversation.checked
      ? conversation.id
      : this._lastCheckedConversationId
  }

  reset = (useRedirect = true) => {
    this._topItemsOrder = []
    this._itemsOrder = []
    this._loadingConversations = false
    this._loading = false
    this._loadingMore = false
    this._hasMore = false
    this._offset = 0
    this._cancelRequestConversation.reset()
    this._cancelRequestConversations.reset()
    this._loadConversationsRequest = null
    this.handleResetSelect()
    this._filtersStore.reset()
    this._ordersStore.reset()

    if (this._visibleUI && !IS_APP_LIGHT && useRedirect) {
      uiStore.changeRoute({
        path: '/conversations',
      })
      this.conversationStore.setCurrentItemId(null)
    }
    this.conversationStore.reset()
    this.conversationStore.resetCurrentItem()
  }

  initReactions = () => {
    this.reactionConversationNewInbox()
    this.reactionFilter()
    this.reactionInbox()
    this.reactionOrder()
    this.reactionLoadingConversations()
  }

  resetReactions = () => {
    this._disposeInbox?.()
    this._disposeConversationNew?.()
    this._disposeLoadingConversations?.()
  }

  handleChangeFirstLoading = (status: boolean) => {
    this._firstLoading = status
  }

  handleChangeInbox = (useRedirect = true) => {
    if (this._entities.inboxesStore.currentInbox instanceof GroupInbox) {
      this.reset()
    }

    if (this._firstLoading || this.conversationStore.currentItemId) {
      this._loading = true
      this.load()
      this._firstLoading = false
    } else {
      if (this._visibleUI && !this.conversationStore.modeNotOpen && !IS_APP_LIGHT && useRedirect) {
        uiStore.changeRoute({
          path: `/${ConversationsPath.root}`,
        })
        this.conversationStore.setCurrentItemId(null)
      }

      this.reset(useRedirect)
      this.load()
    }
  }

  selectNewInbox = async (conversation?: Conversation) => {
    if (this._entities.inboxesStore.isUnifiedInbox) return false
    if (!conversation || conversation.isNew) return false
    if (!this._entities.inboxesStore.currentInboxId) {
      await this._entities.usersStore.initPromise
    }
    if (conversation.inbox_id === this._entities.inboxesStore.currentInboxId) return false
    if (this._entities.inboxesStore.currentInbox?.type === 'inbox_group') return false

    runInAction(() => {
      this._firstLoading = true
    })

    await this._entities.inboxesStore.handleUpdateTeamInbox(conversation.inbox_id)

    return true
  }

  handleChangeVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  setLoading = (value: boolean) => {
    this._loading = value
    this._loadingConversations = value
  }

  load = async () => {
    runInAction(() => {
      this._loadingConversations = true
    })

    try {
      const conversationResult =
        this.conversationStore.currentItem || (await this.loadConversation())

      const isChangeInbox = await this.selectNewInbox(conversationResult)

      if (isChangeInbox) return

      let conversationsResult

      if (this._entities.inboxesStore.localInboxes.currentSmartViewInboxId) {
        conversationsResult = await this._loadConversationsSmartViews(
          this._entities.inboxesStore.localInboxes.currentSmartViewInboxId
        )
      } else {
        conversationsResult = await this._loadConversations()
      }

      this._applyConversations(conversationsResult)

      if (conversationResult) {
        this._applyConversation(conversationResult)
      }
    } catch (error) {
      if (error instanceof AxiosError) return

      this.handleConversationLoadError()
    }

    if (this._visibleUI && !this.conversationStore.modeNotOpen) {
      const { search: query } = window.location

      if (this.listOrders.length) {
        const onloadQuery = new URLSearchParams(window.location.search)

        if (!onloadQuery.has('decline_onload')) {
          onloadQuery.set('onload', '')
        }

        const state = uiStore.location?.state ? { ...uiStore.location?.state } : null
        const isMentionsQuickFilter =
          this._entities.inboxesStore.localInboxes.currentInboxId === 'mentions'

        onloadQuery.delete('decline_onload')

        const recentMentionMessageId = this.conversationStore.currentItemId
          ? this.conversationStore.currentItem?.recentMentionMessage?.id
          : this.listOrders[0]?.recentMentionMessage?.id

        if (
          recentMentionMessageId &&
          !onloadQuery.has('messageId') &&
          !onloadQuery.has('decline_onload') &&
          isMentionsQuickFilter
        ) {
          onloadQuery.set('messageId', String(recentMentionMessageId))
        }
        const onloadQueryString = onloadQuery.toString()

        if (!this.conversationStore.currentItemId) {
          const firstConversation = this.listOrders[0]

          uiStore.changeRoute({
            path: `/conversations/${firstConversation?.id}`,
            query: onloadQueryString,
            options: {
              state,
            },
          })
        } else {
          uiStore.changeRoute({
            path: `/conversations/${this.conversationStore.currentItemId}`,
            query: onloadQueryString,
            options: {
              state,
            },
          })
        }
      } else {
        uiStore.changeRoute({
          path: `/conversations/${this.conversationStore.currentItemId || ''}`,
          query,
        })
      }
    }
    setTimeout(() => {
      runInAction(() => {
        this._loadingConversations = false
      })
    }, 100)
  }

  handleConversationLoadError = () => {
    if (this._visibleUI && !this.conversationStore.modeNotOpen) {
      uiStore.changeRoute({
        path: '/conversations',
      })
    }
  }

  loadConversation = async () => {
    if (!this.conversationStore.currentItemId) return

    this._cancelRequestConversation.init()

    try {
      return await this.conversationStore.getById({
        id: this.conversationStore.currentItemId,
        update: true,
        isModalError: true,
        options: {
          cancelToken: this._cancelRequestConversation.token,
        },
      })
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  checkFilter = (conversation?: Conversation) => {
    if (!conversation) return false

    const item = conversation
    const message = conversation.recent_message || undefined

    return !this._filtersStore.isShowConversation(item, message)
  }

  private _loadConversationsSmartViews = async (id: number) => {
    if (this._loadConversationsRequest) return this._loadConversationsRequest

    if (this._hasMore) this._loadingMore = true
    this._loading = true

    this._cancelRequestConversations.init()

    const currentRequest: Promise<IResponseConversations | undefined> = (async () => {
      try {
        await this._filtersStore.loadFilters()

        const params: IParamsSmartViewConversations = {
          filter: this._filtersStore.filter?.key,
          limit: this._limit,
          offset: this._offset,
          sort_by:
            this._filtersStore.filter?.key === 'outbound'
              ? 'last_message_at'
              : 'last_manual_message_at',
          grouped_response: true,
          order: this._ordersStore.order?.key,
        }

        const response = await SmartViewsApi.getSmartViewConversationsList(id, params, {
          cancelToken: this._cancelRequestConversations.token,
        })
        const data = response.data

        runInAction(() => {
          this._loading = false
          this._loadingMore = false
          this._hasMore = data.conversations.length === this._limit
          if (this._hasMore) this._offset = this._offset + this._limit
        })

        const totalCount = data.conversations_total_count

        this.conversationStore.setItemsTotalCount(totalCount)

        return data
      } catch (error) {
        logger.error(error)

        if (!(error instanceof CanceledError))
          runInAction(() => {
            this._loading = false
            this._loadingMore = false
          })

        throw error
      } finally {
        runInAction(() => {
          if (this._loadConversationsRequest === currentRequest) {
            this._loadConversationsRequest = null
          }
        })
      }
    })()

    this._loadConversationsRequest = currentRequest

    return this._loadConversationsRequest
  }

  private _loadConversations = async () => {
    if (this._loadConversationsRequest) return this._loadConversationsRequest

    if (this._hasMore) this._loadingMore = true
    this._loading = true

    this._cancelRequestConversations.init()

    const currentRequest: Promise<IResponseConversations | undefined> = (async () => {
      try {
        await this._filtersStore.loadFilters()

        const params: IParamsConversationsList = {
          team_id: this._entities.inboxesStore.currentInboxTypeId || 0,
          team_type: this._entities.inboxesStore.currentInboxType || 3,
          filter: this._filtersStore.filter?.key,
          limit: this._limit,
          offset: this._offset,
          sort_by:
            this._filtersStore.filter?.key === 'outbound'
              ? 'last_message_at'
              : 'last_manual_message_at',
          grouped_response: true,
          order: this._ordersStore.order?.key,
        }

        if (this.conversationStore.currentItem) {
          params.exclude = this.conversationStore.currentItemId
        }

        const response = await ConversationsApi.getConversations(params, {
          cancelToken: this._cancelRequestConversations.token,
        })
        const data = response.data

        runInAction(() => {
          this._loading = false
          this._loadingMore = false
          this._hasMore = data.conversations.length === this._limit
          if (this._hasMore) this._offset = this._offset + this._limit
        })

        let totalCount = data.conversations_total_count

        if (params.exclude) {
          const currentConversation = this.conversationStore.getItem(params.exclude)
          const isShowConversation = currentConversation
            ? this._filtersStore.isShowConversation(
                currentConversation,
                currentConversation.recent_message || undefined
              )
            : false

          if (isShowConversation) totalCount += 1
        }

        this.conversationStore.setItemsTotalCount(totalCount)

        return data
      } catch (error) {
        logger.error(error)

        if (!(error instanceof CanceledError))
          runInAction(() => {
            this._loading = false
            this._loadingMore = false
          })

        throw error
      } finally {
        runInAction(() => {
          if (this._loadConversationsRequest === currentRequest) {
            this._loadConversationsRequest = null
          }
        })
      }
    })()

    this._loadConversationsRequest = currentRequest

    return this._loadConversationsRequest
  }

  private _applyConversation = async (conversation: Conversation) => {
    if (this.checkFilter(conversation)) return

    try {
      this.addItemOrder(conversation.id, true)
      this.updateItemOrder(conversation.id)
    } catch (error) {
      if (error instanceof AxiosError) return

      this.handleConversationLoadError()
    }
  }

  private _applyConversations = async (response: IResponseConversations | undefined) => {
    if (!response) return

    const contacts = Object.values(response.contacts)
    const owners = Object.values(response.owners)

    this.conversationStore.addItems(response.conversations)
    this._entities.usersStore.addItems(owners)
    this._entities.contactsStore.addItems(contacts)
    this.addItemsOrder(response.conversations)
  }

  updateConversations = async () => {
    try {
      let conversations

      if (this._entities.inboxesStore.localInboxes.currentSmartViewInboxId) {
        conversations = await this._loadConversationsSmartViews(
          this._entities.inboxesStore.localInboxes.currentSmartViewInboxId
        )
      } else {
        conversations = await this._loadConversations()
      }

      this._applyConversations(conversations)
    } catch (error) {
      if (error instanceof AxiosError) return

      this.handleConversationLoadError()
    }
  }

  undoCloseConversations = async () => {
    const nanoId = nanoid()
    const closeConversationsTimeoutId = setTimeout(async () => {
      this.closeConversations()
      toastStore.remove(nanoId)
    }, 5000)

    toastStore.add({
      id: nanoId,
      title: 'Conversations closed',
      type: 'info',
      action: {
        text: 'Undo',
        onAction: () => {
          clearTimeout(closeConversationsTimeoutId)
          toastStore.remove(nanoId)
        },
      },
    })
  }

  closeConversations = async () => {
    const selectedIds = this.selectedListIds
    const itemsPast = this.listOrders

    try {
      const requestsParamsMap: Map<number, IParamsConversationsBatchQueueClose> = new Map()

      selectedIds.forEach((conversationId) => {
        const conversation = this.conversationStore.getItem(conversationId)
        if (!conversation) return

        const requestParams = requestsParamsMap.get(conversation.inbox_id)
        const ids = requestParams ? [...requestParams.ids, conversationId] : [conversationId]
        const teamId = requestParams ? requestParams.team_id : conversation.inbox_id
        const params: IParamsConversationsBatchQueueClose = {
          team_id: teamId,
          ids: ids,
          delete_all: this.isSelectedAll,
          conversation_filter: this._filtersStore.filter.key,
        }

        requestsParamsMap.set(conversation.inbox_id, params)
        this.conversationStore.deleteItem(conversationId)
      })

      const requestsParamsList = Array.from(requestsParamsMap.values())
      const requests: Promise<AxiosResponse>[] = [
        ...requestsParamsList.map((params) =>
          ConversationsApi.updateConversationsBatchQueueClose(params)
        ),
      ]

      if (this.isSelectedAll || !this.listCount) {
        this.conversationStore.setCurrentItemId(null)
      }

      this.setFocusItem(selectedIds, itemsPast)
      this.handleResetSelect()

      await Promise.all(requests)
    } catch (e) {
      logger.error(e)
    } finally {
      if (!this.isSelectedAll && !this.listCount) {
        await this.updateConversations()
      }
    }
  }

  reopenConversations = async () => {
    const selectedIds = this.selectedListIds
    const itemsPast = this.listOrders

    try {
      const requestsParamsMap: Map<number, IParamsConversationsBatchQueueOpen> = new Map()

      selectedIds.forEach((conversationId) => {
        const conversation = this.conversationStore.getItem(conversationId)
        if (!conversation) return

        const requestParams = requestsParamsMap.get(conversation.inbox_id)
        const ids = requestParams ? [...requestParams.ids, conversationId] : [conversationId]
        const teamId = requestParams ? requestParams.team_id : conversation.inbox_id
        const params: IParamsConversationsBatchQueueOpen = {
          team_id: teamId,
          ids: ids,
          open_all: this.isSelectedAll,
          conversation_filter: this._filtersStore.filter.key,
        }

        requestsParamsMap.set(conversation.inbox_id, params)
        this.conversationStore.deleteItem(conversationId)
      })

      const requestsParamsList = Array.from(requestsParamsMap.values())
      const requests: Promise<AxiosResponse>[] = [
        ...requestsParamsList.map((params) =>
          ConversationsApi.updateConversationsBatchQueueOpen(params)
        ),
      ]

      if (this.isSelectedAll || !this.listCount) {
        this.conversationStore.setCurrentItemId(null)
      }

      this.setFocusItem(selectedIds, itemsPast)
      this.handleResetSelect()

      await Promise.all(requests)
    } catch (e) {
      logger.error(e)
    } finally {
      if (!this.isSelectedAll && !this.listCount) {
        await this.updateConversations()
      }
    }
  }

  transferConversation = (ids: number[], store: TransferConversationModalStore) => {
    this._transferConversationModalStore.openModal(
      ids,
      store,
      this.isSelectedAll,
      this._filtersStore.filter?.key || ''
    )
  }

  readConversations = async (id?: number) => {
    try {
      if (!id) {
        const requestsParamsMap: Map<number, IParamsConversationsBatchQueueRead> = new Map()

        this.selectedListIds.forEach((conversationId) => {
          const conversation = this.conversationStore.getItem(conversationId)
          if (!conversation) return

          const requestParams = requestsParamsMap.get(conversation.inbox_id)

          if (requestParams) {
            const ids = [...requestParams.ids, conversationId]
            const params: IParamsConversationsBatchQueueRead = {
              team_id: requestParams.team_id,
              ids: ids,
              read_all: this.isSelectedAll,
              conversation_filter: this._filtersStore.filter?.key,
            }

            requestsParamsMap.set(conversation.inbox_id, params)
            if (this._filtersStore.isUnread) {
              ids.forEach((id) => this.deleteItemOrder(id))
            }
          } else {
            const params: IParamsConversationsBatchQueueRead = {
              team_id: conversation.inbox_id,
              ids: [conversationId],
              read_all: this.isSelectedAll,
              conversation_filter: this._filtersStore.filter?.key,
            }

            requestsParamsMap.set(conversation.inbox_id, params)
            if (this._filtersStore.isUnread) {
              this.deleteItemOrder(conversationId)
            }
          }
        })

        const requestsParamsList = Array.from(requestsParamsMap.values())
        const requests: Promise<AxiosResponse>[] = [
          ...requestsParamsList.map((params) =>
            ConversationsApi.updateConversationsBatchQueueRead(params)
          ),
        ]

        Promise.all(requests)
      } else {
        const conversation = this.conversationStore.getItem(id)
        if (!conversation) return

        const params: IParamsConversationsBatchQueueRead = {
          team_id: conversation.inbox_id,
          read_all: this.isSelectedAll,
          conversation_filter: this._filtersStore.filter?.key,
          ids: [id],
        }

        await ConversationsApi.updateConversationsBatchQueueRead(params)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        if (!id) this.handleResetSelect()
      })
    }
  }

  unreadConversations = async (id?: number) => {
    try {
      if (!id) {
        const requestsParamsMap: Map<number, IParamsConversationsBatchQueueUnread> = new Map()

        this.selectedListIds.forEach((conversationId) => {
          const conversation = this.conversationStore.getItem(conversationId)
          if (!conversation) return

          const requestParams = requestsParamsMap.get(conversation.inbox_id)

          if (requestParams) {
            const params: IParamsConversationsBatchQueueUnread = {
              team_id: requestParams.team_id,
              ids: [...requestParams.ids, conversationId],
              unread_all: false,
              conversation_filter: this._filtersStore.filter?.key,
            }

            requestsParamsMap.set(conversation.inbox_id, params)
          } else {
            const params: IParamsConversationsBatchQueueUnread = {
              team_id: conversation.inbox_id,
              ids: [conversationId],
              unread_all: false,
              conversation_filter: this._filtersStore.filter?.key,
            }

            requestsParamsMap.set(conversation.inbox_id, params)
          }
        })

        const requestsParamsList = Array.from(requestsParamsMap.values())
        const requests: Promise<AxiosResponse>[] = [
          ...requestsParamsList.map((params) =>
            ConversationsApi.updateConversationsBatchQueueUnread(params)
          ),
        ]

        Promise.all(requests)
      } else {
        const conversation = this.conversationStore.getItem(id)
        if (!conversation) return

        const params: IParamsConversationsBatchQueueUnread = {
          team_id: conversation.inbox_id,
          unread_all: false,
          ids: [id],
          conversation_filter: this._filtersStore.filter?.key,
        }

        await ConversationsApi.updateConversationsBatchQueueUnread(params)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        if (!id) this.handleResetSelect()
      })
    }
  }

  updateConversationsFavorite = async (status?: boolean, id?: number) => {
    const hasStatus = typeof status === 'boolean'

    try {
      if (!id) {
        const requestsParamsMap: Map<number, IParamsConversationsBatchQueueFavorite> = new Map()

        this.selectedListIds.forEach((conversationId) => {
          const conversation = this.conversationStore.getItem(conversationId)
          if (!conversation) return

          const requestParams = requestsParamsMap.get(conversation.inbox_id)

          if (requestParams) {
            const params: IParamsConversationsBatchQueueFavorite = {
              team_id: requestParams.team_id,
              favorite: requestParams.favorite,
              ids: [...requestParams.ids, conversationId],
              all: this.isSelectedAll,
              conversation_filter: this._filtersStore.filter?.key,
            }

            requestsParamsMap.set(conversation.inbox_id, params)
          } else {
            const params: IParamsConversationsBatchQueueFavorite = {
              team_id: conversation.inbox_id,
              favorite: hasStatus ? status : this.selectedHasUnfavoriteConversation,
              ids: [conversationId],
              all: this.isSelectedAll,
              conversation_filter: this._filtersStore.filter?.key,
            }

            requestsParamsMap.set(conversation.inbox_id, params)
          }
        })

        const requestsParamsList = Array.from(requestsParamsMap.values())
        const requests: Promise<AxiosResponse>[] = [
          ...requestsParamsList.map((params) =>
            ConversationsApi.updateConversationsBatchQueueFavorite(params)
          ),
        ]

        Promise.all(requests)
      } else {
        const conversation = this.conversationStore.getItem(id)
        if (!conversation) return

        const params: IParamsConversationsBatchQueueFavorite = {
          team_id: conversation.inbox_id,
          favorite: hasStatus ? status : this.selectedHasUnfavoriteConversation,
          all: this.isSelectedAll,
          ids: [id],
          conversation_filter: this._filtersStore.filter?.key,
        }

        await ConversationsApi.updateConversationsBatchQueueFavorite(params)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        if (!id) this.handleResetSelect()
      })
    }
  }

  updateConversationsTags = () => {
    this._contactsTagsModalStore.onOpen({
      ids: this.selectedContactsListIds,
      isBulkAll: this.isSelectedAll,
      callback: this.handleResetSelect,
      conversation_filter: this._filtersStore.filter?.key,
      teamId: this._entities.inboxesStore.currentInboxId,
    })
  }

  addTopItemOrder = (conversationId: number) => {
    const isExist = this._topItemsOrder.some((orderId) => conversationId === orderId)

    if (!isExist) {
      this._topItemsOrder.push(conversationId)
    }
  }

  deleteTopItemOrder = (conversationId: number) => {
    this._topItemsOrder = this._topItemsOrder.filter((id) => id !== conversationId)
  }

  isInTopItemOrder = (conversationId: number) =>
    this._topItemsOrder.some((id) => id === conversationId)

  addItemsOrder = (items: IResponseConversation[]) => {
    items.forEach((item) => {
      this.addItemOrder(item.id)
    })
  }

  addItemOrder = (conversationId: number, top = false) => {
    const index = this._itemsOrder.findIndex((orderId) => conversationId === orderId)
    const isExist = index !== -1

    if (isExist) {
      if (top) {
        this._itemsOrder.splice(index, 1)
        this._itemsOrder.unshift(conversationId)
      }
    } else {
      if (top) this._itemsOrder.unshift(conversationId)
      else this._itemsOrder.push(conversationId)
    }
  }

  updateItemOrder = (conversationId: number, index = 0, log?: boolean) => {
    const conversation = this.conversationStore.getItem(conversationId)
    const countItems = this._itemsOrder.length
    const countIsFavorite = this._itemsOrder.reduce<number>((state, id) => {
      const item = this.conversationStore.getItem(id)

      return item?.is_favorite ? state + 1 : state
    }, 0)

    const existIndex = this._itemsOrder.findIndex((orderId) => conversationId === orderId)

    if (this._ordersStore.order?.key === 'priority') {
      if (!conversation?.is_favorite) {
        index += countIsFavorite
      }
    }

    if (this._ordersStore.order?.key === 'oldest') {
      index += countItems
    }

    const data = {
      cache: {
        itemsOrder: this._ordersStore,
        existIndex: existIndex,
        index: index,
      },
    }

    if (existIndex !== -1) {
      this._itemsOrder.splice(existIndex, 1)
    }

    this._itemsOrder.splice(index, 0, conversationId)

    if (
      log &&
      this._ordersStore.order?.key === 'newest' &&
      this._itemsOrder[0] !== conversationId
    ) {
      sentEventSentry({
        event: '[Custom Event] updateItemOrder',
        name: 'conversationList',
        level: 'error',
        tags: {
          data: JSON.stringify({
            ...data,
            id: conversationId,
            itemsOrder: this._itemsOrder,
          }),
        },
      })
    }

    this.handleScrollTrigger()
  }

  deleteItemOrder = (value: number) => {
    this._itemsOrder = this._itemsOrder.filter((id) => id !== value)
  }

  refreshConversations = async () => {
    try {
      await this.updateConversations()

      // Handle first conversation. Core flow
      if (this.autoSelectConversationId) {
        const query = new URLSearchParams(window.location.search)
        query.set('onload', '')
        const queryString = query.toString()

        uiStore.changeRoute({
          path: `/conversations/${this.autoSelectConversationId}`,
          query: queryString,
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  searchConversations = async () => {
    try {
      runInAction(() => {
        this._loading = true
        if (this._conversationSearchStore.searchParams.page > 1) this._loadingMore = true
      })

      const { data } = await ConversationsApi.getConversationsSearch(
        this._conversationSearchStore.searchParams
      )

      if (data) {
        if (this._conversationSearchStore.searchParams.page === 1) {
          this.conversationStore.reset()
        }

        runInAction(() => {
          const owners: IResponseUser[] = []
          const contacts: IResponseContact[] = []
          const conversations: IResponseConversation[] = data.results

          this.conversationStore.addItems(conversations)
          this._entities.usersStore.addItems(owners)
          this._entities.contactsStore.addItems(contacts)
          this.addItemsOrder(conversations)

          this._conversationSearchStore.handleNextPage(
            this._conversationSearchStore.searchParams.page + 1
          )
          this._conversationSearchStore.handleTotal(data.total)
        })
      }

      runInAction(() => {
        this._loading = false
        this._loadingMore = false
        this._loadingConversations = false

        const firstConversationId = this.listOrders?.[0]?.id

        if (firstConversationId && this._visibleUI) {
          uiStore.changeRoute({
            path: `/conversations/${firstConversationId}`,
            query: 'onload=',
          })
        }
      })
    } catch (e) {
      logger.error(e)
      if (this._visibleUI) {
        uiStore.changeRoute({
          path: '/conversations',
        })
      }

      runInAction(() => {
        this._loading = false
        this._loadingMore = false
      })
    }
  }

  setFirstItemId = () => {
    const firstItem = this.listOrders[0]

    if (firstItem) {
      this.conversationStore.setNextItemId(firstItem.id)
    } else {
      this.conversationStore.setNextItemId(null)
    }
  }

  setFocusItem = (deleteIds: number[], listItemsPast: Conversation[]) => {
    const currentFocusId = this.conversationStore.currentItemId || 0
    const isDeleteFocus = deleteIds.includes(currentFocusId)

    if (this.listCount === 0) {
      this.conversationStore.setNextItemId(null)
    } else if (isDeleteFocus) {
      if (this.listCount === 1) {
        this.setFirstItemId()
      } else {
        const currentIndexDeletedIds = deleteIds.findIndex((id) => id === currentFocusId)
        const stepsNext = deleteIds.filter((id, index) => currentIndexDeletedIds < index).length
        const stepsPrev = deleteIds.filter((id, index) => currentIndexDeletedIds > index).length
        const currentIndex = listItemsPast.findIndex((item) => item.id === currentFocusId)
        const nextItem = listItemsPast[currentIndex + stepsNext + 1]
        const prevItem = listItemsPast[currentIndex - stepsPrev - 1]

        this.conversationStore.setNextItemId(nextItem?.id || prevItem?.id)
      }
    } else if (!currentFocusId) {
      this.setFirstItemId()
    }

    this.nextNavigate()
  }

  nextNavigate = () => {
    if (this.conversationStore.nextItemId) {
      uiStore.changeRoute({
        path: `/conversations/${this.conversationStore.nextItemId}`,
      })
    } else {
      uiStore.changeRoute({
        path: '/conversations',
      })
    }
  }

  onOpen = async (id: number, itemUndo?: IResponseConversation) => {
    try {
      const itemsPast = this.listOrders

      await ConversationsApi.updateByIdOpen(id)

      if (itemUndo) {
        this.conversationStore.updateItem(itemUndo)
      } else {
        this.conversationStore.deleteItem(id)

        if (!this.listCount) {
          this.conversationStore.setCurrentItemId(null)
          await this.updateConversations()
        }
      }

      this.setFocusItem([id], itemsPast)
    } catch (e) {
      if (e instanceof AxiosError) {
        const conversationId = e.errors.getError('conversation_id')
        if (conversationId) {
          this.confirmAnotherOpenConversation(Number(conversationId))
        } else {
          logger.error(e)
        }
      }
    }
  }

  setFocusItemById = async (id: number) => {
    const itemsPast = this.listOrders
    this.setFocusItem([id], itemsPast)
    await this.updateConversations()
  }

  onManualClose = async (id: number) => {
    try {
      this.onClose(id)

      await ConversationsApi.updateByIdClose(id)
    } catch (e) {
      logger.error(e)
    }
  }

  onClose = async (id: number) => {
    try {
      const itemCache = this.conversationStore.getItem(id)?.origin
      const itemsPast = this.listOrders

      const nanoId = nanoid()

      toastStore.add({
        id: nanoId,
        type: 'info',
        title: 'Conversation closed',
        action: {
          text: 'Undo',
          onAction: () => {
            this.onOpen(id, itemCache)
            this.conversationStore.increaseItemsTotalCount()
            toastStore.remove(nanoId)
          },
        },
      })

      this.conversationStore.deleteItem(id)
      this.conversationStore.decreaseItemsTotalCount()

      if (!this.listCount) {
        this.conversationStore.setCurrentItemId(null)
        await this.updateConversations()
      }

      this.setFocusItem([id], itemsPast)
    } catch (e) {
      logger.error(e)
    }
  }

  confirmAnotherOpenConversation = (conversationId: number) => {
    const id = nanoid()
    toastStore.add({
      id: id,
      type: 'info',
      title: 'Found another open conversation',
      action: {
        text: 'Open',
        onAction: () => {
          toastStore.remove(id)

          uiStore.changeRoute({
            path: `/conversations/${conversationId}`,
          })
          this.conversationStore.setCurrentItemId(conversationId)
          this._filtersStore.setFilter('open', true)
          this.conversationStore.setNextItemId(0)
          this.load()
        },
      },
    })
  }

  subscribeCallBack = () => {
    this.conversationStore.setNextItemId(0)

    if (
      typeof this._entities.inboxesStore.currentInboxId === 'number' &&
      typeof this._entities.inboxesStore.currentInboxType === 'number'
    ) {
      uiStore.changeRoute({
        path: `/${ConversationsPath.root}`,
      })
      this.reset()
      this.refreshConversations()
    }
  }

  handleMarkAsNeedResponse = async ({
    ids,
    isNeedResponse,
    isUndo,
    itemsUndo = [],
  }: IMarkAsNeedResponseParams) => {
    try {
      const isPendingFilter = this._filtersStore.isPending
      const textForTitle = ids.length > 1 ? 'Conversations' : 'Conversation'
      const itemsCache = ids.reduce<IResponseConversation[]>((state, id) => {
        const item = this.conversationStore.getItem(id)

        if (item) state.push(item.origin)

        return state
      }, [])
      const itemsPast = this.listOrders

      if (ids.length === 1) {
        await ConversationsApi.updateConversationsNeedResponseById(ids[0], {
          need_response: isNeedResponse,
        })
      } else {
        await ConversationsApi.updateConversationsBatchQueueNeedResponse({
          ids: ids,
          need_response: isNeedResponse,
        })
      }

      const nanoId = nanoid()

      if (isUndo) {
        toastStore.add({
          id: nanoId,
          type: 'info',
          title: isNeedResponse
            ? `${textForTitle} marked as Needs Response`
            : `${textForTitle} marked as responded`,
          action: {
            text: 'Undo',
            onAction: () => {
              this.handleMarkAsNeedResponse({
                ids: ids,
                isNeedResponse: !isNeedResponse,
                isUndo: false,
                itemsUndo: itemsCache,
              })
              toastStore.remove(nanoId)
            },
          },
        })
      }

      if (isPendingFilter) {
        if (isUndo) {
          ids.forEach((id) => {
            this.conversationStore.deleteItem(id)
          })

          if (this.isSelectedAll || !this.listCount) {
            this.conversationStore.setCurrentItemId(null)
            await this.updateConversations()
          }
        } else {
          itemsUndo.forEach((item) => {
            this.conversationStore.updateItem(item)
          })
        }

        this.setFocusItem(ids, itemsPast)
      } else {
        ids.forEach((id) => {
          this.conversationStore.getItem(id)?.setIsNeedResponse(isNeedResponse)
        })
      }

      this.handleResetSelect()
    } catch (e) {
      logger.error(e)
    }
  }

  onOpenConversation = (item: Conversation) => {
    const goToMessage =
      this._entities.inboxesStore.localInboxes.hasFilterMentions && item.recentMentionMessage
    this.conversationStore.setModeNotOpen(false)
    if (goToMessage) {
      uiStore.changeRoute({
        path: `/conversations/${item.id}`,
        query: `onload=&messageId=${item.recentMentionMessage.id}`,
      })
    } else {
      uiStore.changeRoute({
        path: `/conversations/${item.id}`,
      })
    }

    if (layoutStore.isMobileView) {
      if (this._conversationSearchStore?.hasSearchParams) {
        this._conversationSearchStore.setHide(true)
      }
      this._pageLayoutStore.toEndContainer()
    }
  }
  reactionFilter = () => {
    this._filtersStore.subscribeFilterChange(this.subscribeCallBack)
  }

  reactionOrder = () => {
    this._ordersStore.subscribeOrderChange(() => {
      this.conversationStore.setNextItemId(0)

      if (
        typeof this._entities.inboxesStore.currentInboxId === 'number' &&
        typeof this._entities.inboxesStore.currentInboxType === 'number'
      ) {
        uiStore.changeRoute({
          path: `/${ConversationsPath.root}`,
        })
        this.reset()
        this.refreshConversations()
      }
    })
  }

  reactionInbox = () => {
    this._disposeInbox?.()
    this._disposeInbox = reaction(
      () => this._currentInboxId,
      (value) => {
        if (value === null) return
        if (this._conversationSearchStore.hasSearchParams) return

        this.handleChangeInbox()
      },
      {
        fireImmediately: true,
      }
    )
  }

  changeQuickFilter = (value: string) => {
    if (value === null) return
    if (this._conversationSearchStore.hasSearchParams) return

    this.handleChangeInbox(false)
  }

  reactionLoadingConversations = () => {
    this._disposeLoadingConversations?.()
    this._disposeLoadingConversations = reaction(
      () => this._loadingConversations,
      (value) => {
        this._config?.onChangeLoadingConversations?.(value)
      }
    )
  }

  reactionConversationNewInbox = () => {
    this._disposeConversationNew?.()
    this._disposeConversationNew = reaction(
      () => this.conversationStore.isNewConversation,
      (value) => {
        if (value) {
          this.addTopItemOrder(0)
        } else {
          this.deleteTopItemOrder(0)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  get hasMore() {
    return this._hasMore
  }

  get loading() {
    return this._loading
  }

  get loadingMore() {
    return this._loadingMore
  }

  get loadingConversations() {
    return this._loadingConversations
  }

  get conversationSearchStore() {
    return this._conversationSearchStore
  }

  get conversationAssignDropdownStore() {
    return this._conversationAssignDropdownStore
  }

  private get _currentInboxId() {
    if (this._entities.inboxesStore.localInboxes.currentSmartViewInboxId) {
      return this._entities.inboxesStore.localInboxes.currentSmartViewInboxId
    }

    return this._entities.inboxesStore.currentInboxId
  }
}
